import { selectMaximizedStream } from 'features/layout/features/content/contentSlice';
import { selectPageStreams } from 'features/layout/features/pagination/paginationSlice';
import React, { useContext, useMemo } from 'react';
import { ResizableBox } from 'features/layout/layouts/TiledWithContent/ResizableBox';
import { TileArea } from 'features/layout/pagination/TileArea';
import { useAppSelector } from 'store/hooks';
import { usePaginationObserver } from 'features/layout/usePaginationObserver';
import { Box } from '@mui/material';
import { layoutConfig } from 'utils/layout';
import { Stage } from 'features/layout/Stage/Stage';
import { selectCustomTilesCount } from 'features/layout/features/config/configSlice';
import { RoomLayoutValuesContext } from 'features/layout/Context';

const RoomGrid = () => {
  usePaginationObserver();
  const maximizedStream = useAppSelector(selectMaximizedStream);
  const { verticalLayout, contentWidth } = useContext(RoomLayoutValuesContext);

  const hasContent = !!maximizedStream;

  const streams = useAppSelector(selectPageStreams);
  const customTilesCount = useAppSelector(selectCustomTilesCount);

  const hasTiles = streams.length > 0 || customTilesCount > 0;

  const forcedExpand = hasContent && !hasTiles;

  const verticalFullscreen = verticalLayout && ((hasContent && contentWidth === 1) || forcedExpand);

  const stackedBelowPinned = hasContent && verticalLayout && !verticalFullscreen;

  const gridSettings = useMemo(() => {
    if (!hasContent || (forcedExpand && !verticalFullscreen)) {
      return {
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
      };
    }

    if (verticalLayout) {
      if (verticalFullscreen) {
        return {
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
          alignItems: 'center',
        };
      }

      // Natural rows put the grid right under the pinned tile, and the pair centres as one block.
      return {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto auto',
        alignContent: 'center',
      };
    }

    return {
      gridTemplateColumns:
        contentWidth === 1
          ? '100% 0px'
          : `min(${contentWidth * 100}%, calc(100% - ${layoutConfig.minSidebarWidth}px)) 1fr`,
      gridTemplateRows: '1fr',
    };
  }, [hasContent, contentWidth, verticalLayout, forcedExpand, verticalFullscreen]);

  return (
    <Box
      sx={{
        height: '100%',
        flexGrow: '1',
        maxHeight: '100%',
        display: 'grid',
        ...gridSettings,
      }}
      data-testid="layout-tiled-content"
    >
      {hasContent ? (
        <ResizableBox>
          <Stage />
        </ResizableBox>
      ) : null}
      <Box
        sx={[
          {
            flexGrow: 1,
            alignItems: 'center',
            overflow: 'hidden',
          },
          // Hidden, never unmounted: TileArea hosts AudioStreams, and an empty grid still has audio.
          verticalFullscreen || forcedExpand
            ? {
                display: 'none',
              }
            : {
                display: 'flex',
              },
        ]}
      >
        <TileArea stackedBelowPinned={stackedBelowPinned} />
      </Box>
    </Box>
  );
};

export default RoomGrid;
